/**
 * @file RepMobileNav.js
 * @version 1.3.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-03
 * @modified 2026-10-30
 *
 * The phone navigation for a rep: a bottom tab bar plus a "More" sheet.
 *
 * A bottom bar rather than a drawer because a rep's whole world is six screens.
 * A drawer hides all six behind a tap; a bar shows four for free. And thumb
 * reach matters — the desktop sidebar's 28px rows and its 5px collapse handle
 * are not targets you can hit one-handed in a farmyard.
 *
 * Every tap goes through the navigateToMenu the desktop shell uses, so hash
 * routing, the permission gate and the tab state all keep working unchanged —
 * this component decides nothing about access, it only offers what it was
 * handed.
 */

import React, { useState, useEffect } from 'react';
import './RepMobileNav.css';

/**
 * The four that earn a permanent slot. Everything else lives in More.
 * `perm` is checked against the menu the shell already filtered, so a rep
 * without one of these simply does not get that tab.
 */
const PRIMARY = [
  { id: 'home', label: 'Home', icon: 'home' },
  { id: 'search-customer-orders', label: 'Orders', icon: 'sell' },
  { id: 'search-contacts', label: 'Customers', icon: 'group' },
  { id: 'search-invoices', label: 'Invoices', icon: 'receipt' }
];

/** Offered in the More sheet, in the order a rep is likely to want them. */
const SECONDARY = [
  // 1.2.0 (28 Oct 2026, P7): the lines sent to the rep to price.
  { id: 'rep-pricing', label: 'My Pricing', icon: 'price_check' },
  { id: 'rep-contracts', label: 'My Contracts', icon: 'handshake' },
  { id: 'standing-orders', label: 'Standing Orders', icon: 'event_repeat' },
  { id: 'search-purchase-orders', label: 'Purchase Orders', icon: 'shopping_cart' },
  { id: 'fleet-tracking', label: 'Fleet Tracking', icon: 'location_on' }
];

/**
 * Reachable on a desktop, never on a phone.
 *
 * The Blend Calculator is a dense spreadsheet-style grid and the one place a
 * rep writes; a cramped version invites a mistake in the one screen where a
 * mistake costs something. Formulations is the same shape of problem read-only:
 * forty analysis figures per product, which a phone cannot lay out. Neither is
 * hidden out of squeamishness — both are listed below, disabled, with the
 * reason — so a rep who is looking for one learns where it is rather than
 * concluding it is gone.
 */
const DESKTOP_ONLY = [
  { id: 'blend-calculator', label: 'Blend Calculator', icon: 'calculate',
    why: 'Open on a laptop or tablet' },
  { id: 'formulations', label: 'Formulations', icon: 'science',
    why: 'Open on a laptop or tablet' }
];

function RepMobileNav({
  activeMenu,
  navItemById,
  onNavigate,
  onOpenProfile,
  onLogout,
  theme,
  onToggleTheme,
  appVersion
}) {
  const [moreOpen, setMoreOpen] = useState(false);

  // A tab is offered only if the shell's filtered menu knows it — that map is
  // already permission-filtered, so this needs no access logic of its own.
  const available = (id) => Boolean(navItemById?.[id]);
  const primary = PRIMARY.filter(t => available(t.id));
  const secondary = SECONDARY.filter(t => available(t.id));

  // Close the sheet on navigation, and on Escape.
  useEffect(() => { setMoreOpen(false); }, [activeMenu]);
  useEffect(() => {
    if (!moreOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setMoreOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [moreOpen]);

  const go = (id) => {
    setMoreOpen(false);
    onNavigate(id);
  };

  const inMore = secondary.some(t => t.id === activeMenu);

  return (
    <>
      {moreOpen && (
        <div className="rep-more-scrim" onClick={() => setMoreOpen(false)} role="presentation" />
      )}

      <div className={`rep-more-sheet${moreOpen ? ' open' : ''}`} role="dialog" aria-label="More">
        <div className="rep-more-grip" />
        <div className="rep-more-list">
          {secondary.map(t => (
            <button
              key={t.id}
              type="button"
              className={`rep-more-item${activeMenu === t.id ? ' active' : ''}`}
              onClick={() => go(t.id)}
            >
              <span className="material-symbols-rounded">{t.icon}</span>
              {t.label}
            </button>
          ))}

          {DESKTOP_ONLY.filter(t => available(t.id)).map(t => (
            <button
              key={t.id}
              type="button"
              className="rep-more-item disabled"
              disabled
              title={t.why}
            >
              <span className="material-symbols-rounded">{t.icon}</span>
              {t.label}
              <span className="rep-more-why">{t.why}</span>
            </button>
          ))}

          <div className="rep-more-divider" />

          <button type="button" className="rep-more-item" onClick={onToggleTheme}>
            <span className="material-symbols-rounded">
              {theme === 'light' ? 'dark_mode' : 'light_mode'}
            </span>
            {theme === 'light' ? 'Dark theme' : 'Light theme'}
          </button>
          <button
            type="button"
            className="rep-more-item"
            onClick={() => { setMoreOpen(false); onOpenProfile(); }}
          >
            <span className="material-symbols-rounded">person</span>
            Profile
          </button>
          <button type="button" className="rep-more-item" onClick={onLogout}>
            <span className="material-symbols-rounded">logout</span>
            Log out
          </button>
        </div>
        <div className="rep-more-foot">YOKE v{appVersion}</div>
      </div>

      <nav className="rep-mobile-nav" aria-label="Main">
        {primary.map(t => (
          <button
            key={t.id}
            type="button"
            className={`rep-tab${activeMenu === t.id ? ' active' : ''}`}
            onClick={() => go(t.id)}
            aria-current={activeMenu === t.id ? 'page' : undefined}
          >
            <span className="material-symbols-rounded">{t.icon}</span>
            <span className="rep-tab-label">{t.label}</span>
          </button>
        ))}
        {secondary.length > 0 && (
          <button
            type="button"
            className={`rep-tab${moreOpen || inMore ? ' active' : ''}`}
            onClick={() => setMoreOpen(o => !o)}
            aria-expanded={moreOpen}
          >
            <span className="material-symbols-rounded">more_horiz</span>
            <span className="rep-tab-label">More</span>
          </button>
        )}
      </nav>
    </>
  );
}

export default RepMobileNav;
